import {
  Dispatch,
  MutableRefObject,
  RefObject,
  SetStateAction,
  useEffect,
  useRef,
} from 'react';
import { Dataset, ReferenceControl } from '../../../types/types';
import { mergeInsertedReferenceRow } from '../utils/mergeInsertedReferenceRow';
import { scrollGridToRowSmooth } from '../../../utils/scrollGridToRowSmooth';
import { buildLocateRequestData } from '../../../utils/dataset/buildLocateRequestData';
import { fetchRowIds } from '../../../utils/dataset/fetchRowIds';
import { computeCustomLocateLimit } from '../../../utils/dataset/computeCustomLocateLimit';

interface UseAutoSelectInsertedRowParams {
  autoSelectInsertedId?: string;
  data: any[] | null;
  dataset: Dataset | undefined;
  dataKey: string;
  control: ReferenceControl;
  sessionToken: string | null;
  gridRef: RefObject<HTMLDivElement | any>;
  activeFiltersRef: MutableRefObject<any>;
  // The reference's locked default filter (control.filter) — lives outside
  // activeFilters, so the probe must merge it in to match the grid's scope.
  defaultFilter?: Record<string, any>;
  activeSortingRef: MutableRefObject<any[]>;
  cachedQueryIdRef: MutableRefObject<string>;
  disabledLazyLoadingRef: MutableRefObject<boolean>;
  setAutoSelectResolvedId: Dispatch<SetStateAction<string | undefined>>;
  setSelectedRows: Dispatch<SetStateAction<any[]>>;
  setCachedQueryId: Dispatch<SetStateAction<string>>;
  setDisabledLazyLoading: Dispatch<SetStateAction<boolean>>;
  fetchReferenceData: (
    lazy?: boolean,
    queryId?: string,
    customLimit?: number
  ) => Promise<void>;
}

// Select + scroll to a row just inserted from this reference. autoSelectInsertedId
// is the primary key. If it's on the loaded page, select that row, record its
// control.idItem and scroll to it. Otherwise probe its position (ID-only) and load
// the chunk containing it (custom limit) so this effect re-runs and the found
// branch selects + scrolls. Fail/not-found → leave unselected. Ref-guarded.
export const useAutoSelectInsertedRow = ({
  autoSelectInsertedId,
  data,
  dataset,
  dataKey,
  control,
  sessionToken,
  gridRef,
  activeFiltersRef,
  defaultFilter,
  activeSortingRef,
  cachedQueryIdRef,
  disabledLazyLoadingRef,
  setAutoSelectResolvedId,
  setSelectedRows,
  setCachedQueryId,
  setDisabledLazyLoading,
  fetchReferenceData,
}: UseAutoSelectInsertedRowParams) => {
  // Resolve the inserted row at most once (ref so it can't loop).
  const autoSelectResolvedRef = useRef<boolean>(false);
  // Probe/chunk-fetch in flight for an inserted row not on the loaded page.
  const autoSelectProbingRef = useRef<boolean>(false);

  useEffect(() => {
    if (
      autoSelectResolvedRef.current ||
      !autoSelectInsertedId ||
      !data ||
      !dataset
    ) {
      return;
    }

    const index = data.findIndex(
      (row) => String(row[dataKey]) === String(autoSelectInsertedId)
    );

    if (index !== -1) {
      autoSelectResolvedRef.current = true;
      const loadedRow = data[index];
      setAutoSelectResolvedId(String(loadedRow[control.idItem]));
      setSelectedRows((prev) =>
        mergeInsertedReferenceRow(prev, loadedRow, dataKey, control.multiSelect)
      );
      scrollGridToRowSmooth(gridRef, index);
      return;
    }

    if (autoSelectProbingRef.current) return;
    autoSelectProbingRef.current = true;

    const probeAndLoadInsertedRow = async () => {
      // Post-insert: start from a fresh server cache (mirrors the master's
      // forcedNewData path) so the custom-limit fetch returns a clean, paginable
      // cachedQueryId that later lazy loads continue — otherwise the scroll-
      // triggered lazy load mismatches and fires the newDataLoaded toast.
      setCachedQueryId('');
      cachedQueryIdRef.current = '';
      const lockedCachedQueryId = cachedQueryIdRef.current;

      const { idItem, resolvedFilters, resolvedSorting, requestData } =
        buildLocateRequestData(
          dataset,
          activeFiltersRef.current,
          activeSortingRef.current,
          sessionToken,
          undefined,
          lockedCachedQueryId || null,
          undefined,
          defaultFilter
        );
      const probe = await fetchRowIds({
        dataset,
        requestData,
        resolvedFilters,
        resolvedSorting,
        idItem,
        sessionToken,
        additionalFilterObject: defaultFilter,
      });

      if (!probe) {
        autoSelectResolvedRef.current = true; // give up — do not select
        return;
      }

      // Probe cache differs from the locked cache → stale index (skipped when
      // locked is empty, i.e. the fresh-cache path above).
      if (
        lockedCachedQueryId &&
        probe.cachedQueryId &&
        probe.cachedQueryId !== lockedCachedQueryId
      ) {
        autoSelectResolvedRef.current = true; // give up — do not select
        return;
      }

      const targetIndex = probe.ids.findIndex(
        (row) => String(row?.[idItem]) === String(autoSelectInsertedId)
      );
      if (targetIndex === -1) {
        autoSelectResolvedRef.current = true; // give up — do not select
        return;
      }

      // No clamp to a total: a stale total would shrink the window below the
      // chunk that holds the target row. The server returns only what exists,
      // so over-requesting is harmless.
      const limit = computeCustomLocateLimit(targetIndex);
      await fetchReferenceData(false, lockedCachedQueryId, limit);

      if (probe.ids.length <= limit) {
        setDisabledLazyLoading(true);
        disabledLazyLoadingRef.current = true;
      }
    };
    probeAndLoadInsertedRow();
  }, [
    data,
    autoSelectInsertedId,
    dataset,
    dataKey,
    control.idItem,
    control.multiSelect,
  ]);
};
